<template>
  <div>

    <!-- Floating Trigger Button -->
    <div
      class="audit-float-btn"
      :class="{ 'audit-float-btn--disabled': !personId && !firmaId && !einsatzId && !angebotId && !dokumentId }"
      @click="open"
    >
      <font-awesome-icon icon="fa-duotone fa-clock-rotate-left" class="audit-float-icon" />
      <span class="audit-float-label">
        History
        <span v-if="entryCount !== null">({{ entryCount }})</span>
      </span>
    </div>

    <!-- Overlay -->
    <transition name="audit-fade">
      <div
        v-if="visible"
        class="audit-overlay"
        @click="close"
      />
    </transition>

    <!-- Panel -->
    <transition name="audit-panel">
      <div v-if="visible" class="audit-panel">

        <div class="audit-header">
          <div class="audit-header-left">
            <font-awesome-icon icon="fa-duotone fa-clock-rotate-left" class="audit-header-icon" />
            <div>
              <span class="audit-title">History</span>
              <div class="audit-subtitle">{{ subtitle }}</div>
            </div>
          </div>
          <button class="close" @click="close">
            <font-awesome-icon icon="fa-solid fa-xmark" />
          </button>
        </div>

        <div class="audit-body">

          <div v-if="isBusy" class="audit-empty">
            <font-awesome-icon
              icon="fa-regular fa-clock"
              class="audit-empty-icon"
              spin
            />
            <span>Wird geladen...</span>
          </div>

          <div v-else-if="entries.length === 0" class="audit-empty">
            <font-awesome-icon
              icon="fa-regular fa-clock"
              class="audit-empty-icon"
            />
            <span>Keine Änderungen erfasst</span>
          </div>

          <div v-else>
            <div
              v-for="entry in entries"
              :key="entry.id"
              class="audit-entry"
              :class="'audit-entry--' + entry.action.toLowerCase()"
            >
              <div class="audit-entry-top">
                <span
                  class="audit-badge"
                  :class="'audit-badge--' + entry.action.toLowerCase()"
                >
                  {{ entry.action }}
                </span>
                <span v-if="entry.section" class="audit-section-label">
                  {{ $t(entry.section) }}
                </span>
                <span class="audit-date">{{ entry.createdAt }}</span>
              </div>

              <div class="audit-entry-user">
                <font-awesome-icon icon="fa-regular fa-user" class="mr-1" />
                {{ entry.userLogin ? entry.userLogin + ' (' + entry.userName + ')' : entry.userName || '—' }}
              </div>

              <div
                v-if="entry.details && entry.details.length > 0"
                class="audit-toggle"
                @click="toggleDetails(entry.id)"
              >
                {{ expandedIds.includes(entry.id) ? $t('global.hidedetails') : $t('global.showdetails') }}
                <font-awesome-icon
                  :icon="expandedIds.includes(entry.id)
                    ? 'fa-solid fa-chevron-up'
                    : 'fa-solid fa-chevron-down'"
                  class="ml-1"
                />
              </div>

              <div v-if="expandedIds.includes(entry.id)" class="audit-details">
                <table class="audit-table">
                  <thead>
                    <tr>
                      <th>{{ $t('global.field') }}</th>
                      <th>{{ $t('global.oldvalue') }}</th>
                      <th>{{ $t('global.newvalue') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(detail, i) in entry.details"
                      :key="i"
                      :class="i % 2 === 0 ? 'audit-row-even' : 'audit-row-odd'"
                    >
                      <td class="audit-field">{{ detail.fieldAppKey ? $t('global.' + detail.fieldAppKey) : detail.fieldName }}</td>
                      <td class="audit-old">
                        <em v-if="detail.oldValue == null" class="audit-null">—</em>
                        <font-awesome-icon v-else-if="detail.oldValue === 'true'" icon="fa-solid fa-check" class="audit-bool-true" />
                        <font-awesome-icon v-else-if="detail.oldValue === 'false'" icon="fa-solid fa-xmark" class="audit-bool-false" />
                        <span v-else>{{ resolveValue(detail.oldValue) }}</span>
                      </td>
                      <td class="audit-new">
                        <em v-if="detail.newValue == null" class="audit-null">—</em>
                        <font-awesome-icon v-else-if="detail.newValue === 'true'" icon="fa-solid fa-check" class="audit-bool-true" />
                        <font-awesome-icon v-else-if="detail.newValue === 'false'" icon="fa-solid fa-xmark" class="audit-bool-false" />
                        <span v-else>{{ resolveValue(detail.newValue) }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import AppApi from '@/api/AppApi'
import { apiErrorToAlert } from '@/utils/Errorhandler'

export default {
  name: 'AuditLogPanel',
  props: {
    personId:  { type: String, default: null },
    firmaId:   { type: String, default: null },
    einsatzId: { type: String, default: null },
    angebotId: { type: String, default: null },
    dokumentId: { type: String, default: null },
    subtitle:  { type: String, default: '' },
    lookupMap: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      visible: false,
      isBusy: false,
      entries: [],
      expandedIds: [],
      entryCount: null,
    }
  },
  watch: {
    personId(val)   { if (val) this.loadCount() },
    firmaId(val)    { if (val) this.loadCount() },
    einsatzId(val)  { if (val) this.loadCount() },
    angebotId(val)  { if (val) this.loadCount() },
    dokumentId(val) { if (val) this.loadCount() },
  },
  mounted() {
    if (this.personId || this.firmaId || this.einsatzId || this.angebotId || this.dokumentId) this.loadCount()
  },
  methods: {
    open() {
      if (!this.personId && !this.firmaId && !this.einsatzId && !this.angebotId && !this.dokumentId) return
      this.visible = true
      this.load()
    },
    close() {
      this.visible = false
      this.expandedIds = []
      this.entries = []
    },
    recordParams() {
      if (this.personId) return { person: this.personId }
      if (this.firmaId) return { firma: this.firmaId }
      if (this.einsatzId) return { einsatz: this.einsatzId }
      if (this.angebotId) return { angebot: this.angebotId }
      return { dokument: this.dokumentId }
    },
    loadCount() {
      const params = { ...this.recordParams(), count: 'true' }
      AppApi.get('audit-log/', { params })
        .then(response => {
          this.entryCount = response.data.count
        })
        .catch(() => {})
    },
    load() {
      if (!this.personId && !this.firmaId && !this.einsatzId && !this.angebotId && !this.dokumentId) return
      this.isBusy = true
      this.entries = []
      const params = this.recordParams()
      AppApi.get('audit-log/', { params })
        .then(response => {
          this.entries = response.data
          this.entryCount = this.entries.length
        })
        .catch(e => {
          this.$notify(apiErrorToAlert(e))
        })
        .finally(() => {
          this.isBusy = false
        })
    },
    resolveValue(val) {
      if (val == null) return null
      const s = String(val)
      const dateTimeMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/)
      if (dateTimeMatch) {
        const [, y, m, d, h, min, sec] = dateTimeMatch
        return h ? `${d}.${m}.${y} ${h}:${min}:${sec}` : `${d}.${m}.${y}`
      }
      if (s.includes(',')) {
        return s.split(',').map(v => this.lookupMap[v.trim()] || v.trim()).join(', ')
      }
      const plain = this.stripHtml(s)
      return this.lookupMap[plain] || plain
    },
    stripHtml(s) {
      if (!/<[a-z][\s\S]*>/i.test(s)) return s
      return s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    },
    toggleDetails(id) {
      if (this.expandedIds.includes(id)) {
        this.expandedIds = this.expandedIds.filter((x) => x !== id)
      } else {
        this.expandedIds.push(id)
      }
    },
  },
}
</script>
